import { Box, Grid, Typography } from '@mui/material';
import { Components, Controls } from 'lib';
import { INotify } from 'lib/types';
import { useEffect, useState, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { profileActionCreators } from 'redux/actionCreators';
import { personalInfoSelector } from 'redux/selectors/profile';
import axios, { withCredentials } from 'util/axiosInstance';
import { convertDateToMonthYearDate, stringToLocalDate, stringToUTCDate } from 'util/date.utils';
import { ThemeContext } from 'util/themes/themeContext';
import {
    IProfileLicensuresObject,
    IReduxDataConfig,
} from '../../../../@types/profile-types';
import {
    formDefaults,
    formSchemaResolver,
    ILicenseFormInputs,
} from './licenseForm.helper';
import OrganisationForm, { organisationFormFieldNames } from 'pages/profile/components/OrganisationForm';
import moment from 'moment';
import { Button } from 'lib/controls';
import { useHistory } from 'react-router-dom';
import { DeleteModal } from 'pages/profile/contact/components/DeleteModal';

interface ILicenseForm {
    modalOpen?: boolean;
    handleModalClose?: Function;
    modalEditData?: IProfileLicensuresObject | null;
    licenseReduxConfig: IReduxDataConfig;
    setNotify:Function;
    openDeleteModal?: any;
    handleClose?: any;
}
const styles = {
    btn: {
        minWidth: '272px',
    },
    btnContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '20px',
    },
    title: (selectedTheme: any) => ({
        color: selectedTheme?.palette?.primary[10],
    }),
};
const formFieldNames = ['name', 'entityName', 'number', 'issueDate','expirationDate'];
export const LicenseForm = (props: ILicenseForm) => {
    const { modalOpen, modalEditData, licenseReduxConfig, setNotify, openDeleteModal, handleClose } = props;
    const history = useHistory();
    const dispatch = useDispatch();
    const { selectedTheme } = useContext(ThemeContext);
    const [buttonLoading, setButtonLoading] = useState<boolean>(false);
    const licenseDeletemodalConfig = {
        title: 'Delete License',
        subtitle: 'Are you sure you want to delete this license?',
        btnText: 'Yes, delete this license.',
    }
    const inputStyleProp = {
        ...selectedTheme?.typography?.Components?.inputText?.default,
        ...selectedTheme?.typography?.Components?.inputText?.dark,
    };
    const labelStyleProp = {
        ...selectedTheme?.typography?.Components?.inputLabel?.default,
        ...selectedTheme?.typography?.Components?.inputLabel?.dark,
        marginBottom: '5px',
    };

    const personalInfo = useSelector(
        personalInfoSelector.selectPersonalInfoData
    );

    const [defaultFormData, setDefaultFormData] =
        useState<ILicenseFormInputs>(formDefaults);
   
    const [isLoading, setLoading] = useState<boolean>(false);

    const {
        handleSubmit,
        reset: formReset,
        setFocus,
        control,
        formState: { errors },
    } = useForm<ILicenseFormInputs>({
        resolver: formSchemaResolver,
        defaultValues: defaultFormData,
    });

    const submitForm = async (formValues: any) => {
        const rawFormData = JSON.parse(JSON.stringify(formValues));
        const keys = Object.keys(formDefaults);
        const requestValues: any = {};
        for (const field of keys) {
            requestValues[field] = rawFormData[field];
        }
        requestValues.issueDate = stringToUTCDate(requestValues.issueDate);
        requestValues.expirationDate = stringToUTCDate(
            requestValues.expirationDate
        );
        setLoading(true);
        setButtonLoading(true);
        try {
            if (!defaultFormData.asset_id) {
                delete requestValues.asset_id;
                await axios().put(
                    licenseReduxConfig.endpoint,
                    requestValues,
                    withCredentials()
                );
                setLoading(false);
                setButtonLoading(false);
                setNotify({ message: 'License successfully added.', type: 'success', isOpen: true });
            } else {
                requestValues.asset_id = defaultFormData.asset_id;
                requestValues.userId = personalInfo.userId;
                if (formValues.asset_id && formValues.asset_id !== "") {
                    requestValues.createDate = modalEditData?.createDate ? modalEditData?.createDate : null;
                }
                delete requestValues.asset_id;
                await axios().put(
                    `${licenseReduxConfig.endpoint}/${defaultFormData.asset_id}`,
                    requestValues,
                    withCredentials()
                );
                setLoading(false);
                setButtonLoading(false);
                setNotify({ message: 'License successfully Changed.', type: 'success', isOpen: true });
            }
            dispatch(
                profileActionCreators.getPersonalDynamicCardsInfo(
                    licenseReduxConfig
                )
            );
            setTimeout(()=>{
                history.push('/profile')
            },3000)
        } catch (error) {
            setLoading(false);
            setButtonLoading(false);
            setNotify({
                message: 'An error occurred. Please try later!',
                type: 'error',
                isOpen: true,
            });
        }
    };

    useEffect(() => {
        const formData: ILicenseFormInputs = JSON.parse(
            JSON.stringify(formDefaults)
        );
        if (modalEditData && modalEditData.asset_id) {
            Object.assign(formData, modalEditData);
            formData.issueDate = stringToLocalDate(modalEditData.issueDate);
            formData.expirationDate = stringToLocalDate(
                modalEditData.expirationDate
            );
        } else {
            formData.issueDate = '';
            formData.expirationDate = '';
        }
        formReset(formData);
        setDefaultFormData(formData);
    }, [modalEditData]);

    const onDelBtnClick = async () => {
        await axios().delete(
            `${licenseReduxConfig.endpoint}/${defaultFormData.asset_id}`,
            withCredentials()
        );
        handleClose();
        setTimeout(() => {
            history.push('/profile');
        }, 2000);
    }
    const getDeleteDataFormat = (datas: any) => {
        const { name, entityName, number, issueDate, expirationDate } = datas;
        return (
            <>
                {name && `Name: ${name}`} <br />
                {number && `Number: ${number}`} <br />
                {entityName && `Entity Name: ${entityName}`}<br />
                {issueDate && `Issue Date: ${convertDateToMonthYearDate(issueDate)}`} <br />
                {expirationDate && `Expiration Date: ${convertDateToMonthYearDate(expirationDate)}`}<br />
            </>
        )
    }
    useEffect(() => {
        const errorKeys = Object.keys(errors) as Array<keyof typeof errors>;
        let firstFieldName: keyof typeof errors | undefined;
        ([...formFieldNames, ...organisationFormFieldNames] as (keyof ILicenseFormInputs)[]).forEach(fieldName => {
            if(!firstFieldName && errorKeys.includes(fieldName)) {
                firstFieldName = fieldName;
            }
        })
    
        if (firstFieldName) {
          setFocus(firstFieldName);
        }
    }, [errors, setFocus]);
    return (
        <>
            <Typography
                marginBottom={'20px'}
                sx={{
                    ...selectedTheme?.typography?.h2,
                    ...styles.title(selectedTheme),
                }}
            >
                License Information
            </Typography>
            <form id="license_form" onSubmit={handleSubmit(submitForm)}>
                <Grid container mb={2}>
                    <Grid item xs={12}>
                        <Controls.Input
                            name={formFieldNames[0]}
                            label="License Name"
                            defaultValue={defaultFormData.name}
                            control={control}
                            error={errors?.name || ''}
                            inputStyleProps={inputStyleProp}
                            sx={{ ...selectedTheme?.input?.dark }}
                            labelStyles={labelStyleProp}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Controls.Input
                            name={formFieldNames[1]}
                            label="Licensure Entity"
                            defaultValue={defaultFormData.entityName}
                            control={control}
                            error={errors?.entityName || ''}
                            inputStyleProps={inputStyleProp}
                            sx={{ ...selectedTheme?.input?.dark }}
                            labelStyles={labelStyleProp}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Controls.Input
                            name={formFieldNames[2]}
                            label="License Number"
                            defaultValue={defaultFormData.number}
                            control={control}
                            error={errors?.number || ''}
                            inputStyleProps={inputStyleProp}
                            sx={{ ...selectedTheme?.input?.dark }}
                            labelStyles={labelStyleProp}
                            required
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Box paddingRight={1}>
                            <Controls.DatePickerComponent
                                name={formFieldNames[3]}
                                label="Issue Date"
                                defaultValue={defaultFormData.issueDate}
                                control={control}
                                error={errors?.issueDate || ''}
                                inputStyleProps={inputStyleProp}
                                inputProps={{
                                    min: moment().subtract('120', 'years'),
                                    max: moment(),
                                }}
                                sx={{
                                    ...selectedTheme?.input?.dark,
                                    '& .MuiSvgIcon-root': {
                                        color: '#CCD0FF',
                                    },
                                }}
                                labelStyles={labelStyleProp}
                                required
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box paddingLeft={1}>
                            <Controls.DatePickerComponent
                                name={formFieldNames[4]}
                                label="Expiration Date"
                                defaultValue={defaultFormData.expirationDate}
                                control={control}
                                error={errors?.expirationDate || ''}
                                inputStyleProps={inputStyleProp}
                                inputProps={{
                                    min: moment().subtract('120', 'years'),
                                }}
                                sx={{
                                    ...selectedTheme?.input?.dark,
                                    '& .MuiSvgIcon-root': {
                                        color: '#CCD0FF',
                                    },
                                }}
                                labelStyles={labelStyleProp}
                            />
                        </Box>
                    </Grid>
                </Grid>
                {OrganisationForm.mainForm({
                    defaultFormData,
                    control,
                    errors,
                })}
                <Box sx={styles.btnContainer}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        sx={styles.btn}
                        loading={buttonLoading}
                        disabled={buttonLoading}
                        profileButtonDisabled={true}
                    >
                        <Typography
                            sx={{
                                ...selectedTheme?.typography?.Components?.button
                                    ?.default,
                                ...selectedTheme?.typography?.Components?.button
                                    ?.large,
                                textTransform: 'none',
                                color: selectedTheme?.palette?.primary?.main,
                            }}
                        >
                            {buttonLoading ? "Saving" : "Save"}
                        </Typography>
                    </Button>
                </Box>
            </form>
            <DeleteModal
                title={licenseDeletemodalConfig.title}
                subtitle={licenseDeletemodalConfig.subtitle}
                btnText={licenseDeletemodalConfig.btnText}
                data={getDeleteDataFormat(defaultFormData)}
                show={openDeleteModal}
                onCloseClick={handleClose}
                onDeleteClick={onDelBtnClick}
            />
        </>
    );
};
